import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '../../ui/Modal';

export interface CreateField {
  name: string;
  label: string;
  type: 'text' | 'url' | 'password' | 'select';
  placeholder?: string;
  hint?: string;
  required?: boolean;
  defaultValue?: string;
  options?: { value: string; label: string }[];
}

export interface CreateEntityDialogProps {
  isOpen: boolean;
  title: string;
  fields: CreateField[];
  onConfirm: (values: Record<string, string>) => void;
  onCancel: () => void;
  confirmLabel?: string;
  loading?: boolean;
  error?: string | null;
}

export const CreateEntityDialog: React.FC<CreateEntityDialogProps> = ({
  isOpen,
  title,
  fields,
  onConfirm,
  onCancel,
  confirmLabel = '创建',
  loading = false,
  error = null,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const initial: Record<string, string> = {};
      fields.forEach(f => {
        initial[f.name] = f.defaultValue || '';
      });
      setValues(initial);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, fields]);

  const handleChange = useCallback((name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(values);
  }, [onConfirm, values]);

  const footer = (
    <>
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        disabled={loading}
      >
        取消
      </button>
      <button
        type="submit"
        form="create-entity-form"
        className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
        disabled={loading}
      >
        {loading ? '创建中...' : confirmLabel}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      footer={footer}
      size="sm"
      closeOnOverlayClick={!loading}
    >
      <form id="create-entity-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        {fields.map(field => (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.type === 'select' ? (
              <select
                value={values[field.name] || field.defaultValue || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {field.options?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type={field.type}
                value={values[field.name] ?? ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            {field.hint && (
              <p className="mt-1 text-xs text-gray-500">{field.hint}</p>
            )}
          </div>
        ))}
      </form>
    </Modal>
  );
};

export default CreateEntityDialog;
